<script setup>
import { computed, defineModel, useAttrs, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import WithLabel from './WithLabel.vue';

const { locale } = useI18n();
const isRtl = computed(() => ['fa', 'ar'].includes(locale.value));
const emSpace = '\u2003';
const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    required: true,
  },
  hasError: Boolean,
  errorMessage: {
    type: String,
    default: '',
  },
  spacing: {
    type: String,
    default: 'base',
    validator: value => ['base', 'compact'].includes(value),
  },
  isFirstItem: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  placeholderMarquee: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  forceErrorDirection: {
    type: String,
    default: 'auto',
    validator: value => ['auto', 'ltr', 'rtl'].includes(value),
  },
  showCharacterCount: { type: Boolean, default: false },
  maxLength: { type: Number, default: null },
});
const emit = defineEmits(['blur']);
defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();
const inputAttrs = computed(() => {
  if (!props.placeholderMarquee) return attrs;
  const { placeholder, ...rest } = attrs;
  return rest;
});

const model = defineModel({
  type: [String, Number],
  required: true,
});

const characterCount = computed(() =>
  typeof model.value === 'string' ? model.value.length : 0
);

const maxLengthForDisplay = computed(
  () => props.maxLength ?? attrs.maxlength
);
</script>

<template>
  <WithLabel
    :label="label"
    :icon="icon"
    :name="name"
    :required="required"
    :has-error="hasError"
    :error-message="errorMessage"
    :force-error-direction="forceErrorDirection"
    :character-count="showCharacterCount && maxLengthForDisplay != null ? characterCount : null"
    :max-length="showCharacterCount ? maxLengthForDisplay : null"
    :class="{ 'mt-6': !isFirstItem }"
    :disabled="disabled"
  >
    <template #rightOfLabel>
      <slot />
    </template>
    <div class="relative w-full">
      <input
        v-bind="placeholderMarquee ? inputAttrs : attrs"
        v-model="model"
        @blur="emit('blur')"
        class="reset-base block w-full !h-12 border-1 border-[#BDBDBD] dark:border-slate-700 min-[1440px]:rounded-xl rounded-lg shadow-sm appearance-none outline outline-1 focus:outline focus:outline-1 text-slate-900 dark:text-slate-100 placeholder:text-[#9A9AA2] dark:placeholder:text-slate-400 sm:text-sm sm:leading-6 !bg-transparent dark:!bg-transparent"
        :class="{
          'focus:outline-red-600 dark:focus:outline-red-500 outline-red-600 dark:outline-red-500': hasError,
          'outline-slate-200 dark:outline-slate-600 dark:focus:outline-woot-500 focus:outline-woot-500': !hasError,
          'px-4 py-3': spacing === 'base',
          'px-3 py-2 mb-0': spacing === 'compact',
          'pl-9': icon,
          '!text-[#9A9AA2]': disabled,
        }"
      />
      <div
        v-if="placeholderMarquee && !model"
        class="absolute inset-0 flex items-center overflow-hidden pointer-events-none text-[#9A9AA2] dark:text-slate-400 sm:text-sm"
        :class="{
          'px-4 py-3': spacing === 'base' && !icon,
          'pl-9 pr-4 py-3': spacing === 'base' && icon,
          'px-3 py-2': spacing === 'compact',
        }"
        :dir="isRtl ? 'rtl' : 'ltr'"
      >
        <span
          class="inline-block whitespace-nowrap select-none"
          :class="isRtl ? 'animate-placeholder-marquee-rtl' : 'animate-placeholder-marquee-ltr'"
          :style="{ width: 'max-content' }"
        >
          {{ attrs.placeholder }}&#x2003;{{ attrs.placeholder }}
        </span>
      </div>
    </div>
  </WithLabel>
</template>

<style scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  background-color: transparent !important;
  -webkit-text-fill-color: currentColor !important;
  transition: background-color 9999s ease-out 0s;
}
</style>
